<template>
  <q-section
    v-bind="$attrs"
    :class="`q-button ${isPressed ? 'q-button--pressed' : ''}`"
    @click="handleClick"
    @mousedown="handleMouseDown"
    @mouseup="handleMouseUp"
    @mouseleave="handleMouseLeave"
    @touchstart="handleTouchStart"
    @touchend="handleTouchEnd">
    <slot />
  </q-section>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import QSection from './q-section.vue'
import throttle from 'lodash/throttle'

// 定义组件名称
defineOptions({
  name: 'QButton',
  inheritAttrs: false,
})

// Props 定义
interface Props {
  // 点击动画持续时间（毫秒）
  animationDuration?: number
  // 缩放比例
  scale?: number
  // 是否禁用
  disabled?: boolean
  // 点击节流时间
  throttleTime?: number
}

const props = withDefaults(defineProps<Props>(), {
  animationDuration: 50,
  scale: 0.9,
  throttleTime: 500,
})

// Emits 定义
const emit = defineEmits<{
  click: [event: MouseEvent | TouchEvent]
}>()

// 响应式状态
const isPressed = ref(false)
const animationTimer = ref<number | null>(null)

// 处理点击事件：仅转发事件，不触发按下动画，避免无法还原
const handleClick = throttle((event: MouseEvent | TouchEvent) => {
  if (props.disabled) return
  emit('click', event)
}, props.throttleTime)

// 处理鼠标按下
const handleMouseDown = (event: MouseEvent) => {
  console.log('handleMouseDown')
  if (props.disabled) return
  triggerPressAnimation()
}

// 处理鼠标抬起
const handleMouseUp = (event: MouseEvent) => {
  console.log('handleMouseUp')
  if (props.disabled) return
  triggerReleaseAnimation()
}

// 处理鼠标离开
const handleMouseLeave = (event: MouseEvent) => {
  console.log('handleMouseLeave')
  if (props.disabled) return
  triggerReleaseAnimation()
}

// 处理触摸开始
const handleTouchStart = (event: TouchEvent) => {
  if (props.disabled) return
  triggerPressAnimation()
}

// 处理触摸结束
const handleTouchEnd = (event: TouchEvent) => {
  if (props.disabled) return
  triggerReleaseAnimation()
}

// 触发按下动画
const triggerPressAnimation = () => {
  // 清除之前的定时器
  if (animationTimer.value) {
    clearTimeout(animationTimer.value)
  }

  isPressed.value = true
}

// 触发释放动画
const triggerReleaseAnimation = () => {
  // 清除之前的定时器
  if (animationTimer.value) {
    clearTimeout(animationTimer.value)
    animationTimer.value = null
  }

  // 立即恢复，不需要延迟
  isPressed.value = false
}
// 组件卸载时清理定时器
import { onUnmounted, onMounted } from 'vue'

// 页面失去焦点时恢复状态
const handleVisibilityChange = () => {
  if (document.hidden && isPressed.value) {
    triggerReleaseAnimation()
  }
}

onMounted(() => {
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
  if (animationTimer.value) {
    clearTimeout(animationTimer.value)
  }
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>

<style lang="scss" scoped>
.q-button {
  cursor: pointer;
  transition: transform v-bind('`${animationDuration}ms`') ease-in-out;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    /* 可以添加悬停效果 */
  }

  &.q-button--pressed {
    transform: scale(v-bind('scale'));
  }

  // 禁用状态
  &[disabled] {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

// 确保按钮在触摸设备上有良好的体验
@media (hover: none) and (pointer: coarse) {
  .q-button {
    -webkit-tap-highlight-color: transparent;
  }
}
</style>
